import type {
  KorintModulesProductsConfig,
  FormSections,
  FormSectionsWithShortcuts,
  CustomFunnelConfig,
  FieldShortcut,
} from 'korint-sdk/react';
import { CustomFunnelAuth } from 'korint-sdk/react';
import {
  AlertTriangle,
  BadgeCheck,
  BookOpenCheck,
  Building2,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  Euro,
  FilePen,
  FileUp,
  FolderOpen,
  Globe,
  Hash,
  Home,
  Key,
  Layers,
  LayoutGrid,
  Mail,
  MapPin,
  Maximize2,
  PartyPopper,
  Percent,
  Phone,
  Shield,
  User,
  Wallet,
  XCircle,
} from 'lucide-react';
import { ContactKey } from '@folio/utils/customerDisplayName';
import {
  policyControllerConfirmPolicy,
  policyControllerGeneratePolicySignatureRequest,
  policyControllerPushPolicyEvent,
} from 'korint-sdk';
import { DocumentsUploadPolicyModule, InvoicesScheduleModule } from '@folio/modules';
import { createAnonymousGeneratedDocumentsStep } from '../../shared/anonymousGeneratedDocumentsStep';
import { tutassurHeader } from '../layout/header';
import { tutassurFooter } from '../layout/footer';
import z, { type ZodTypeAny } from 'zod';

// ============================================================================
// Form Sections
// ============================================================================
const assetFunnelFields: FieldShortcut[] = [
  { name: 'assetAddress', icon: MapPin, type: 'address' },
  { name: 'assetAddressComplement', icon: MapPin },
  { name: 'assetPostalCode', colSpan: 4 as const, icon: Hash },
  { name: 'assetCity', type: 'city', colSpan: 8 as const, icon: Building2 },
  {
    name: 'assetLotType',
    icons: {
      APARTMENT: Building2,
      HOUSE: Home,
    },
  },
  {
    name: 'assetFloor',
    icon: Layers,
    type: 'number' as const,
    integer: true,
    display: 'cards' as const,
    displayMin: 0,
    displayMax: 9,
    labels: { 0: 'RDC' },
  },
  {
    name: 'assetMainRoomsNumber',
    icon: LayoutGrid,
    type: 'number' as const,
    integer: true,
    display: 'cards' as const,
    displayMin: 1,
    displayMax: 10,
  },
  { name: 'assetSurface', icon: Maximize2 },
];

const killingQuestionField = (name: string) => ({
  name,
  blockingValue: 'true',
  blockingErrorMessage: 'Il est impossible de souscrire une police dans ce cas',
  additionalValidationRules: (schema: ZodTypeAny) =>
    schema.and(
      z.literal('false', {
        errorMap: () => ({ message: 'Il est impossible de souscrire une police dans ce cas' }),
      }),
    ),
});

const policyFunnelSections: FormSectionsWithShortcuts = [
  {
    title: "Déclarations de l'assuré",
    fields: [
      { name: 'isMovingIn', icon: Key, display: 'toggle' as const },
      {
        ...killingQuestionField('hadAtLeastTwoClaimsInTheLastTwoYears'),
        icon: AlertTriangle,
        display: 'toggle' as const,
        showWhen: [{ fieldName: 'isMovingIn', condition: 'equals', value: 'false' }],
      },
      {
        ...killingQuestionField('hadPreviousContractCancelledByInsurer'),
        icon: XCircle,
        display: 'toggle' as const,
      },
    ],
  },
] satisfies FormSectionsWithShortcuts;

const customerFunnelSections = [
  {
    title: 'Informations du signataire',
    fields: [
      { name: 'contactCivility', icons: { MONSIEUR: User, MADAME: User, MADEMOISELLE: User } },
      { name: 'contactFirstName', colSpan: 6 as const, icon: User },
      { name: 'contactLastName', colSpan: 6 as const, icon: User },
      { name: 'contactEmail', colSpan: 6 as const, icon: Mail },
      { name: 'contactPhoneNumber', colSpan: 6 as const, icon: Phone },
      { name: 'contactBirthDate', colSpan: 4 as const, icon: CalendarDays },
      {
        name: 'contactBirthCity',
        type: 'city',
        addressGroup: 'contactBirth',
        colSpan: 4 as const,
        icon: MapPin,
      },
      {
        name: 'contactBirthCountry',
        type: 'country',
        addressGroup: 'contactBirth',
        colSpan: 4 as const,
        icon: Globe,
      },
      { name: 'contactAddress', icon: MapPin, type: 'address' },
      { name: 'contactPostCode', type: 'postcode', colSpan: 4 as const, icon: Hash },
      { name: 'contactCity', type: 'city', colSpan: 8 as const, icon: Building2 },
    ],
  },
] satisfies FormSectionsWithShortcuts;

const quoteAssetSections = [
  {
    title: "Date de prise d'effet de l'assurance",
    fields: [
      {
        name: 'startedAt',
        required: true,
        type: 'datetime',
        min: 'now',
        icon: CalendarDays,
      },
    ],
  },
] as const satisfies FormSectionsWithShortcuts;

const quotePolicySections: FormSectionsWithShortcuts = [
  {
    title: 'Franchise',
    fields: [
      {
        name: 'chosenExcess',
        required: true,
        type: 'select',
        options: [
          { value: '170', label: '170 €' },
          { value: '290', label: '290 €' },
        ],
        icons: { '170': Euro, '290': Euro },
        isCustomField: true,
        apiDataAccessor: (apiData: { customFields?: Record<string, unknown> }) =>
          apiData.customFields?.chosenExcess?.toString() ?? '290',
        formDataAccessor: (formData: Record<string, unknown>) => Number(formData.chosenExcess),
      },
    ],
  },
] satisfies FormSectionsWithShortcuts;

const additionalQuoteComponentsSections = [
  {
    title: 'Frais et commissions grossiste',
    forTypes: ['WHOLESALE'],
    fields: [
      {
        name: 'wholesaleAdditionalCommission',
        isCustomField: true,
        type: 'number',
        required: false,
        min: 0,
        max: 100,
        colSpan: 6 as const,
        icon: Percent,
      },
      {
        name: 'wholesaleSubscriptionFee',
        isCustomField: true,
        type: 'number',
        required: false,
        min: 0,
        colSpan: 6 as const,
        icon: Euro,
        apiDataAccessor: (apiData: { customFields: Record<string, number> }) =>
          (apiData.customFields.wholesaleSubscriptionFee ?? 0) / 100,
        formDataAccessor: (formData: Record<string, unknown>) =>
          Number(formData.wholesaleSubscriptionFee ?? 0) * 100,
      },
    ],
  },
  {
    title: 'Frais et commissions distributeur',
    forTypes: ['RETAIL'],
    fields: [
      {
        name: 'retailFee',
        type: 'number',
        required: false,
        min: 0,
        max: 100,
        colSpan: 6 as const,
        icon: Percent,
        apiDataAccessor: (apiData: {
          brokerageFees?: { brokerageFirmId: string; fee: number }[];
          brokerageFirm?: string;
        }) =>
          apiData.brokerageFees?.find(
            ({ brokerageFirmId }) => brokerageFirmId === apiData.brokerageFirm,
          )?.fee ?? 0,
        formDataAccessor: (formData: Record<string, unknown>) => ({
          fees: [
            {
              brokerageFirmId: formData.brokerageFirm,
              fee: Number(formData.retailFee ?? 0),
            },
          ],
        }),
      },
      {
        name: 'retailSubscriptionFee',
        isCustomField: true,
        type: 'number',
        required: false,
        min: 0,
        colSpan: 6 as const,
        icon: Euro,
        apiDataAccessor: (apiData: { customFields: Record<string, number> }) =>
          (apiData.customFields.retailSubscriptionFee ?? 0) / 100,
        formDataAccessor: (formData: Record<string, unknown>) =>
          Number(formData.retailSubscriptionFee ?? 0) * 100,
      },
      {
        name: 'retailRenewalFee',
        isCustomField: true,
        type: 'number',
        required: false,
        min: 0,
        icon: CalendarDays,
        apiDataAccessor: (apiData: { customFields: Record<string, number> }) =>
          (apiData.customFields.retailRenewalFee ?? 0) / 100,
        formDataAccessor: (formData: Record<string, unknown>) =>
          Number(formData.retailRenewalFee ?? 0) * 100,
      },
    ],
  },
] as const satisfies Array<FormSections[number] & { forTypes?: string[] }>;

// ============================================================================
// Anonymous Funnel Steps
// ============================================================================
const anonymousDocumentsStep = {
  path: 'documents',
  type: 'module' as const,
  module: (
    <DocumentsUploadPolicyModule
      displayContext="policyholder"
      documentsLists={[
        { category: 'id-card', required: true },
        { category: 'iban', required: true },
        { category: 'rental-lease' },
        { category: 'autre-document' },
      ]}
    />
  ),
};

const anonymousGeneratedDocumentsStep = createAnonymousGeneratedDocumentsStep({
  categories: ['MRH_QUOTE', 'MRH_TERMS_AND_CONDITIONS', 'MRH_IPID', 'MRH_INFORMATION_SHEET'],
  requiredDocuments: [
    <span>Votre pièce d'identité</span>,
    <span>Votre RIB (IBAN)</span>,
    <span>Votre bail de location (optionnel)</span>,
    <span>Tout autre document utile (optionnel)</span>,
  ],
  label: 'Documents',
  icon: FolderOpen,
});

const BILLING_FREQUENCY_ICONS = {
  MONTHLY: CalendarDays,
  QUARTERLY: CalendarRange,
  HALF_YEARLY: CalendarClock,
  YEARLY: CalendarCheck,
};

// ============================================================================
// Anonymous Funnel Base Definitions
// ============================================================================

type AnonymousFunnelBase = Omit<
  CustomFunnelConfig,
  'name' | 'basePath' | 'afterEndPath' | 'layout' | 'customLayout'
>;

const tutassurCustomLayout = {
  header: tutassurHeader,
  footer: tutassurFooter,
};

const anonymousDiscoveryBase: AnonymousFunnelBase = {
  auth: CustomFunnelAuth.ANONYMOUS,
  steps: [
    {
      path: 'asset',
      type: 'assetFunnel',
      icon: Home,
      maxAssets: 1,
      fields: assetFunnelFields,
    },
    {
      path: 'customer',
      type: 'customerFunnel',
      icon: User,
      sections: customerFunnelSections,
      triggerEvent: 'TUTASSUR_ANONYMOUS_PRICING_COMPLETED',
      maxCustomers: 1,
      minCustomers: 1,
    },
    {
      path: 'policy',
      type: 'policyFunnel',
      icon: BookOpenCheck,
      sections: policyFunnelSections,
    },
    {
      path: 'billing',
      type: 'billingSelectionFunnel',
      icon: CalendarClock,
      frequencyIcons: BILLING_FREQUENCY_ICONS,
    },
    {
      path: 'quote',
      type: 'quoteFunnel',
      icon: Shield,
      assetSections: quoteAssetSections,
      policySections: quotePolicySections,
      additionalQuoteComponentsSections,
    },
    {
      path: 'invoicing-schedule',
      type: 'module',
      icon: CalendarDays,
      module: <InvoicesScheduleModule />,
    },
    anonymousGeneratedDocumentsStep,
  ],
};

const anonymousSubscriptionBase: AnonymousFunnelBase = {
  auth: CustomFunnelAuth.ANONYMOUS,
  steps: [
    anonymousDocumentsStep,
    {
      path: 'payment-methods',
      type: 'paymentMethodFunnel',
      icon: Wallet,
    },
    {
      path: 'end',
      type: 'funnelEnd' as const,
      id: 'anonymous-subscription-end',
      icon: PartyPopper,
      callback: {
        callToActionCallback: async ({
          policyId,
          branchId,
        }: {
          policyId: string;
          branchId: string;
        }) => {
          await policyControllerPushPolicyEvent(
            policyId,
            { eventType: 'TUTASSUR_NB_QUOTE_EMITTED' },
            { branchId },
          );
          await policyControllerConfirmPolicy(policyId, { branchId });
          for (let attempt = 0; attempt < 100; attempt++) {
            try {
              await policyControllerGeneratePolicySignatureRequest(
                policyId,
                { deliveryMode: 'EMAIL', isOtp: false, phoneNumber: '' },
                { branchId },
              );
              break;
            } catch (error: unknown) {
              if ((error as any)?.errorResponse?.status === 425 && attempt < 99) {
                await new Promise(resolve => setTimeout(resolve, 1000));
                continue;
              }
              throw error;
            }
          }
        },
      },
      redirectTo: ({ policyId, branchId }: { policyId: string; branchId: string }) =>
        `/auth?redirect=${encodeURIComponent(`/policyholder/${policyId}/${branchId}`)}`,
      additionalComponents: {
        isQuoteTierModuleVisible: false,
        finishWithoutSendingButton: {
          enabled: false,
        },
      },
    },
  ],
};

// ============================================================================
// Product Configuration
// ============================================================================
export const tutassurMilaMultirisqueHabitationLocataireConfig = {
  display: {
    category: 'habitation',
    icon: Home,
    badges: ['online'],
  },
  productCustomPath: '/mrh-locataire-delegated',

  // ============================================================================
  // Customer Information Display
  // ============================================================================
  customerInformations: {
    fieldsConfig: [
      'contactCivility',
      { key: 'contactFirstName', contactKey: ContactKey.FIRST_NAME },
      { key: 'contactLastName', contactKey: ContactKey.LAST_NAME },
      'contactEmail',
      'contactBirthDate',
      'contactBirthCity',
      'contactBirthCountry',
      'contactPhoneNumber',
      'contactAddress',
      'contactPostCode',
      'contactCity',
    ],
  },

  // ============================================================================
  // Asset Information Display
  // ============================================================================
  assetInformations: {
    fieldsConfig: [
      'assetAddress',
      'assetAddressComplement',
      'assetPostalCode',
      'assetCity',
      'assetLotType',
      'assetSurface',
      'assetFloor',
      'assetMainRoomsNumber',
    ],
  },

  // ============================================================================
  // Quote & Premium Display
  // ============================================================================
  priceBreakdownConfig: {
    groups: [
      { lines: [{ key: 'INSURANCE_PREMIUM', variant: 'bold' }] },
      {
        lines: [{ key: 'WHOLESALE_ADDITIONAL_COMMISSION', variant: 'bold' }],
      },
      {
        lines: [{ key: 'KORINT_MANAGEMENT_FEE', variant: 'bold' }],
      },
      {
        lines: [{ key: 'RETAIL_COMMISSION', variant: 'bold' }],
      },
      {
        variant: 'callout',
        lines: [
          { key: 'ATTACK_TAX', variant: 'dimmed' },
          { key: 'SUBSCRIPTION_FEE', variant: 'dimmed' },
          { key: 'WHOLESALE_SUBSCRIPTION_FEE', variant: 'dimmed' },
          { key: 'RENEWAL_FEE', variant: 'dimmed' },
          { key: 'RETAIL_SUBSCRIPTION_FEE', variant: 'dimmed' },
          { key: 'RETAIL_RENEWAL_FEE', variant: 'dimmed' },
        ],
      },
    ],
  },
  quoteTierModuleConfig: {
    goToTiersOnMta: '/mta/mrh-locataire-delegated/${policy.customer}/${policy.id}/new',
    goToTiersOnNewBusiness:
      '/discovery/mrh-locataire-delegated/${policy.customer}/${policy.id}/${workingBranch?.id}/quote',
    fieldsConfig: [],
    perilsMapping: {},
    isExternalQuoteEnabled: false,
    isChosenTierDisplayed: true,
  },

  // ============================================================================
  // Discovery Funnel (New Business)
  // ============================================================================
  discoveryFunnelSteps: {
    afterEndPath: undefined,
    steps: [
      {
        path: 'documents-upload',
        type: 'documentUploadFunnel' as const,
        context: 'policy' as const,
        icon: FileUp,
      },
      {
        path: 'asset',
        type: 'assetFunnel',
        icon: Home,
        maxAssets: 1,
        fields: assetFunnelFields,
      },
      {
        path: 'customer',
        type: 'customerFunnel',
        icon: User,
        sections: customerFunnelSections,
      },
      {
        path: 'policy',
        type: 'policyFunnel',
        icon: BookOpenCheck,
        sections: policyFunnelSections,
      },
      {
        path: 'billing',
        type: 'billingSelectionFunnel',
        icon: CalendarClock,
        frequencyIcons: BILLING_FREQUENCY_ICONS,
      },
      {
        path: 'quote',
        type: 'quoteFunnel',
        icon: Shield,
        assetSections: quoteAssetSections,
        policySections: quotePolicySections,
        additionalQuoteComponentsSections,
      },
      {
        path: 'invoicing-schedule',
        type: 'module',
        icon: CalendarDays,
        module: <InvoicesScheduleModule />,
      },
      {
        path: 'compliance',
        type: 'complianceFunnel',
        icon: BadgeCheck,
      },
      {
        path: 'end',
        type: 'funnelEnd',
        id: 'new-business-end',
        icon: PartyPopper,
        additionalComponents: {
          isQuoteTierModuleVisible: true,
        },
        callback: {
          callToActionCallback: async ({ policyId, branchId }) => {
            await policyControllerPushPolicyEvent(
              policyId,
              { eventType: 'TUTASSUR_NB_QUOTE_EMITTED' },
              { branchId },
            );
          },
        },
      },
    ],
  },

  // ============================================================================
  // Anonymous Funnels (Policyholder Self-Service)
  // ============================================================================
  customFunnels: [
    {
      ...anonymousDiscoveryBase,
      name: 'anonymous-discovery',
      basePath:
        '/anonymous/:brokerageFirmId/mrh-locataire-delegated/:customerId/:policyId/:branchId?',
      afterEndPath:
        '/anonymous/:brokerageFirmId/mrh-locataire-delegated-souscription/:customerId/:policyId/:branchId?/documents',
      layout: 'custom' as const,
      customLayout: tutassurCustomLayout,
    },
    {
      ...anonymousDiscoveryBase,
      name: 'anonymous-discovery-embedded',
      basePath:
        '/anonymous-embedded/:brokerageFirmId/mrh-locataire-delegated/:customerId/:policyId/:branchId?',
      afterEndPath:
        '/anonymous-embedded/:brokerageFirmId/mrh-locataire-delegated-souscription/:customerId/:policyId/:branchId?/documents',
      layout: 'embedded' as const,
    },
    {
      ...anonymousSubscriptionBase,
      name: 'anonymous-subscription',
      basePath:
        '/anonymous/:brokerageFirmId/mrh-locataire-delegated-souscription/:customerId/:policyId/:branchId?',
      layout: 'custom' as const,
      customLayout: tutassurCustomLayout,
    },
    {
      ...anonymousSubscriptionBase,
      name: 'anonymous-subscription-embedded',
      basePath:
        '/anonymous-embedded/:brokerageFirmId/mrh-locataire-delegated-souscription/:customerId/:policyId/:branchId?',
      layout: 'embedded' as const,
    },
  ],

  // ============================================================================
  // MTA Funnel (Mid-Term Adjustment)
  // ============================================================================
  mtaFunnelSteps: {
    steps: [
      {
        path: 'new',
        type: 'newMTACreation',
        icon: FilePen,
      },
      {
        path: 'asset',
        type: 'assetFunnel',
        icon: Home,
        maxAssets: 1,
        fields: assetFunnelFields,
      },
      {
        path: 'policy',
        type: 'policyFunnel',
        icon: BookOpenCheck,
        sections: policyFunnelSections,
      },
      {
        path: 'billing',
        type: 'billingSelectionFunnel',
        icon: CalendarClock,
        frequencyIcons: BILLING_FREQUENCY_ICONS,
      },
      {
        path: 'quote',
        type: 'quoteFunnel',
        icon: Shield,
        policySections: quotePolicySections,
        additionalQuoteComponentsSections,
      },
      {
        path: 'invoicing-schedule',
        type: 'module',
        icon: CalendarDays,
        module: <InvoicesScheduleModule />,
      },
      {
        path: 'customer',
        type: 'customerFunnel',
        icon: User,
        sections: customerFunnelSections,
      },
      {
        path: 'compliance',
        type: 'complianceFunnel',
        icon: BadgeCheck,
      },

      {
        path: 'end',
        type: 'funnelEnd',
        id: 'mta-end',
        icon: PartyPopper,
        additionalComponents: {
          isQuoteTierModuleVisible: true,
        },
        callback: {},
      },
    ],
  },

  // ============================================================================
  // Policy Dashboard Configuration
  // ============================================================================
  brokerPolicyDashboardHome: {
    isFinalizeNewBusinessModuleVisible: true,
    isAuthorCardModuleVisible: true,
    isPolicyholderInvitationModuleVisible: true,
    isQuoteTierModuleVisible: true,
    isPremiumModuleVisible: true,
    isPolicyLifecycleSummaryModuleVisible: true,
    isInvoicesBalanceModuleVisible: true,
  },
  policyDashboardEventsModuleConfig: {
    enableAssetComparison: true,
    enablePolicyComparison: false,
  },
  policyholderInvitationModuleConfig: {
    relationTypes: ['POLICYHOLDER'],
  },

  // ============================================================================
  // Finalize New Business
  // ============================================================================
  finalizeNewBusinessModuleConfig: {
    buttons: [
      {
        id: 'send-via-email',
        actionType: 'signature',
        visual: {
          label: 'Envoi du contrat au client',
          icon: 'mail',
          successMessage: 'Contrat envoyé par email',
        },
        signature: {
          signatureType: 'direct',
          deliveryMode: 'EMAIL',
          otp: {
            enabled: false,
          },
        },
        visibilityConditions: {
          policyStatuses: ['POLICY_QUOTED'],
        },
      },
    ],
    rules: [
      {
        id: 'documents-import',
        link: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}/documents`,
        isValid: ({ policy }) => policy.customFields.allClientDocumentsOk === true,
      },
      {
        id: 'sepa-payment',
        link: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}/payment-methods`,
        isValid: ({ paymentMethods }) =>
          paymentMethods.some(({ paymentMethodType }) => paymentMethodType === 'sepa_debit'),
      },
    ],
  },

  // ============================================================================
  // Policy Information Card
  // ============================================================================
  policyInformationCard: {
    title: "Déclarations de l'assuré",
    fieldsConfig: [
      {
        key: 'chosenExcess',
        type: 'text',
        formatValue: value => (value !== undefined ? `${value} €` : undefined),
        hideIfEmpty: true,
      },
      {
        key: 'isMovingIn',
        type: 'boolean',
        formatValue: value => (value === true ? 'Oui' : 'Non'),
      },
      {
        key: 'hadAtLeastTwoClaimsInTheLastTwoYears',
        type: 'boolean',
        formatValue: value => (value === true ? 'Oui' : 'Non'),
      },
      {
        key: 'hadPreviousContractCancelledByInsurer',
        type: 'boolean',
        formatValue: value => (value === true ? 'Oui' : 'Non'),
      },
    ],
  },
} satisfies KorintModulesProductsConfig;
